// ============================================================================
// MODULAR DESIGN PRINCIPLES (MANDATORY)
// ============================================================================
// This file strictly follows:
// 1. Single Responsibility Principle - Each component does ONE thing
// 2. Separation of Concerns - Logic separated from presentation
// 3. Modular Architecture - Independent, reusable components
//
// Refer to: docs/architecture/modular-design-standards.md
// ============================================================================

// Refer to docs/implementation/qa-criteria-system.md

import React from 'react';
import {
  Box,
  Card,
  CardContent,
  TextField,
  Button,
  IconButton,
  Typography,
  Stack,
  Divider,
  Alert,
  LinearProgress,
  Chip
} from '@mui/material';
import {
  Add,
  Delete,
  ExpandMore,
  ExpandLess,
  Warning
} from '@mui/icons-material';
import MetricEditor from './MetricEditor';

/**
 * CategoryEditor Component
 * 
 * Editable category with:
 * - Category name, weight, description
 * - List of metrics (MetricEditor components)
 * - Add/Remove metrics
 * - Real-time weight validation
 */
const CategoryEditor = ({ 
  category, 
  index, 
  onChange, 
  onDelete, 
  error,
  expanded = true,
  onToggleExpand,
  criteriaType = 'matrix'
}) => {
  const [isExpanded, setIsExpanded] = React.useState(expanded);

  const handleChange = (field, value) => {
    onChange(index, { ...category, [field]: value });
  };

  const handleMetricChange = (metricIndex, updatedMetric) => {
    const newMetrics = [...(category.metrics || [])];
    newMetrics[metricIndex] = updatedMetric;
    handleChange('metrics', newMetrics);
  };

  const handleAddMetric = () => {
    const newMetrics = [...(category.metrics || [])];
    newMetrics.push({
      metric_name: '',
      metric_key: `metric-${Date.now()}`,
      description: '',
      weight_percentage: 0,
      keywords: [],
      min_evidence_items: 1,
      display_order: newMetrics.length,
      ...(criteriaType === 'boolean'
        ? { possible_scores: ['Yes', 'No', 'N/A'], is_mandatory: true }
        : {})
    });
    handleChange('metrics', newMetrics);
  };

  const handleDeleteMetric = (metricIndex) => {
    const newMetrics = category.metrics.filter((_, idx) => idx !== metricIndex);
    handleChange('metrics', newMetrics);
  };

  // Calculate total metric weight
  const totalMetricWeight = (category.metrics || []).reduce(
    (sum, metric) => sum + (metric.weight_percentage || 0),
    0
  );

  const weightError = totalMetricWeight !== (category.weight_percentage || 0);

  return (
    <Card 
      sx={{ 
        mb: 2,
        border: error ? '2px solid #d32f2f' : '1px solid #e0e0e0'
      }}
    >
      <CardContent>
        {/* Category Header */}
        <Stack direction="row" spacing={2} alignItems="flex-start" sx={{ mb: 2 }}>
          {/* Category Number & Expand Button */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Typography variant="h6" color="primary">
              #{index + 1}
            </Typography>
            <IconButton
              size="small"
              onClick={() => setIsExpanded(!isExpanded)}
            >
              {isExpanded ? <ExpandLess /> : <ExpandMore />}
            </IconButton>
          </Box>

          {/* Category Fields */}
          <Box sx={{ flex: 1 }}>
            <Stack spacing={1.5}>
              {/* Category Name */}
              <TextField
                label="Category Name"
                value={category.category_name || ''}
                onChange={(e) => handleChange('category_name', e.target.value)}
                size="small"
                fullWidth
                required
                error={!!error?.category_name}
                helperText={error?.category_name}
              />

              {/* Description */}
              <TextField
                label="Description"
                value={category.description || ''}
                onChange={(e) => handleChange('description', e.target.value)}
                size="small"
                fullWidth
                multiline
                rows={2}
              />

              {/* Weight */}
              <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
                <TextField
                  label="Category Weight %"
                  type="number"
                  value={category.weight_percentage || 0}
                  onChange={(e) => handleChange('weight_percentage', parseInt(e.target.value) || 0)}
                  size="small"
                  sx={{ width: 150 }}
                  required
                  error={!!error?.weight_percentage}
                  helperText={error?.weight_percentage}
                  inputProps={{ min: 0, max: 100, step: 1 }}
                />

                {/* Metrics Count */}
                <Chip
                  label={`${category.metrics?.length || 0} metrics`}
                  size="small"
                  color={category.metrics?.length > 0 ? 'primary' : 'default'}
                  variant="outlined"
                />

                {/* Weight Status */}
                <Chip
                  label={`Metrics: ${totalMetricWeight}%`}
                  size="small"
                  color={weightError ? 'error' : 'success'}
                  icon={weightError ? <Warning /> : undefined}
                />
              </Box>
            </Stack>
          </Box>

          {/* Delete Button */}
          <Box>
            <IconButton
              onClick={() => onDelete(index)}
              color="error"
              title="Delete category"
            >
              <Delete />
            </IconButton>
          </Box>
        </Stack>

        {/* Metrics Section (Collapsible) */}
        {isExpanded && (
          <>
            <Divider sx={{ my: 2 }} />

            {/* Weight Progress Bar */}
            <Box sx={{ mb: 2 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                <Typography variant="caption" color="text.secondary">
                  Total Metric Weight
                </Typography>
                <Typography 
                  variant="caption" 
                  color={weightError ? 'error' : 'success.main'}
                  fontWeight="medium"
                >
                  {totalMetricWeight}% / {category.weight_percentage || 0}%
                </Typography>
              </Box>
              <LinearProgress
                variant="determinate"
                value={Math.min((totalMetricWeight / (category.weight_percentage || 100)) * 100, 100)}
                color={weightError ? 'error' : 'success'}
                sx={{ height: 6, borderRadius: 1 }}
              />
              {weightError && (
                <Typography variant="caption" color="error" sx={{ mt: 0.5, display: 'block' }}>
                  Metric weights must sum to exactly {category.weight_percentage || 0}% (category weight)
                </Typography>
              )}
            </Box>

            {/* Metrics List */}
            <Typography variant="subtitle2" gutterBottom sx={{ mt: 2 }}>
              Metrics
            </Typography>

            {!category.metrics || category.metrics.length === 0 ? (
              <Alert severity="info" sx={{ mb: 2 }}>
                No metrics defined. Click "Add Metric" to create one.
              </Alert>
            ) : (
              <Box sx={{ mb: 2 }}>
                {category.metrics.map((metric, metricIndex) => (
                  <MetricEditor
                    key={metricIndex}
                    metric={metric}
                    index={metricIndex}
                    onChange={handleMetricChange}
                    onDelete={handleDeleteMetric}
                    error={error?.metrics?.[metricIndex]}
                    criteriaType={criteriaType}
                  />
                ))}
              </Box>
            )}

            {/* Add Metric Button */}
            <Button
              variant="outlined"
              startIcon={<Add />}
              onClick={handleAddMetric}
              size="small"
            >
              Add Metric
            </Button>
          </>
        )}

        {/* Category Error Message */}
        {error && typeof error === 'string' && (
          <Alert severity="error" sx={{ mt: 2 }}>
            {error}
          </Alert>
        )}
      </CardContent>
    </Card>
  );
};

export default CategoryEditor;
